/**
 * Pure helpers for the pricing-v2 product metadata (`tier` / `access` /
 * `rank`). The Stripe Product carries these as a flat
 * `metadata: Record<string, string>`; the catalog UI works with the
 * structured `ProductMetadata` shape and round-trips through here.
 *
 * Kept side-effect free so it can be unit-tested in isolation — see
 * `product-metadata.test.ts` (this module is on the vitest coverage
 * include list).
 */
import {
  PRODUCT_ACCESS_OPTIONS,
  PRODUCT_METADATA_ACCESS_KEY,
  PRODUCT_METADATA_RANK_KEY,
  PRODUCT_METADATA_TIER_KEY,
  PRODUCT_RANK_MAX,
  PRODUCT_RANK_MIN,
  PRODUCT_TIERS,
  type ProductAccess,
  type ProductTier,
} from "./constants";

/** Structured view of a Product's pricing-v2 metadata. */
export interface ProductMetadata {
  readonly tier: ProductTier | null;
  readonly access: readonly ProductAccess[];
  readonly rank: number | null;
}

/**
 * Form-state shape. `rank` stays a raw string so the controlled
 * `<input type="number">` can hold partial / invalid text without the
 * dialog losing keystrokes.
 */
export interface ProductMetadataValue {
  readonly tier: ProductTier | "";
  readonly access: readonly ProductAccess[];
  readonly rank: string;
}

/**
 * Whether a Product is wired up for subscriptions:
 * - `subscription-ready` — tier + rank + at least one access value.
 * - `incomplete` — some metadata set but not all; the public catalog
 *   would exclude it. Surfaced as a warning in the products list.
 * - `non-subscription` — no pricing-v2 metadata at all (a legitimate
 *   non-subscription Product); no warning.
 */
export type CatalogReadiness = "subscription-ready" | "incomplete" | "non-subscription";

const TIER_SET: ReadonlySet<string> = new Set(PRODUCT_TIERS);
const ACCESS_SET: ReadonlySet<string> = new Set(PRODUCT_ACCESS_OPTIONS);

/** Strict positive-integer parse — rejects floats, signs, and junk. */
function parseRank(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(n) || n < PRODUCT_RANK_MIN || n > PRODUCT_RANK_MAX) {
    return null;
  }
  return n;
}

/** Split the `access` CSV into the canonical, deduped, known-value list. */
function parseAccess(raw: string | undefined): readonly ProductAccess[] {
  if (!raw) return [];
  const seen = new Set<string>();
  for (const token of raw.split(",")) {
    const value = token.trim().toLowerCase();
    if (ACCESS_SET.has(value)) seen.add(value);
  }
  // Canonical order so the round-tripped CSV is stable.
  return PRODUCT_ACCESS_OPTIONS.filter((opt) => seen.has(opt));
}

/** Parse a raw Stripe `metadata` bag into the structured shape. */
export function parseProductMetadata(
  metadata: Readonly<Record<string, string>> | null | undefined,
): ProductMetadata {
  const bag = metadata ?? {};
  const tierRaw = bag[PRODUCT_METADATA_TIER_KEY]?.trim().toUpperCase();
  return {
    tier: tierRaw && TIER_SET.has(tierRaw) ? (tierRaw as ProductTier) : null,
    access: parseAccess(bag[PRODUCT_METADATA_ACCESS_KEY]),
    rank: parseRank(bag[PRODUCT_METADATA_RANK_KEY]),
  };
}

/** Seed a form value from parsed metadata. */
export function productMetadataToValue(meta: ProductMetadata): ProductMetadataValue {
  return {
    tier: meta.tier ?? "",
    access: meta.access,
    rank: meta.rank === null ? "" : String(meta.rank),
  };
}

/** Interpret a form value as structured metadata (invalid rank → null). */
export function valueToProductMetadata(value: ProductMetadataValue): ProductMetadata {
  return {
    tier: value.tier === "" ? null : value.tier,
    access: PRODUCT_ACCESS_OPTIONS.filter((opt) => value.access.includes(opt)),
    rank: parseRank(value.rank),
  };
}

/**
 * Is the rank field acceptable to submit? Empty is allowed (a
 * non-subscription Product); a non-empty value must be a valid integer.
 */
export function isRankInputValid(rank: string): boolean {
  return rank.trim() === "" || parseRank(rank) !== null;
}

/** Classify a Product's catalog readiness for the products-list warning. */
export function catalogReadiness(meta: ProductMetadata): CatalogReadiness {
  const hasTier = meta.tier !== null;
  const hasRank = meta.rank !== null;
  const hasAccess = meta.access.length > 0;
  if (hasTier && hasRank && hasAccess) return "subscription-ready";
  if (!hasTier && !hasRank && !hasAccess) return "non-subscription";
  return "incomplete";
}

/** Equality over the structured metadata — used for the update dirty-diff. */
export function productMetadataEquals(a: ProductMetadata, b: ProductMetadata): boolean {
  return (
    a.tier === b.tier &&
    a.rank === b.rank &&
    a.access.length === b.access.length &&
    a.access.every((value, idx) => value === b.access[idx])
  );
}

/**
 * Build the `metadata` bag for a Product **create**. Only set keys are
 * included — an all-empty value yields `undefined` so the request omits
 * `metadata` entirely (a non-subscription Product).
 */
export function buildCreateMetadata(
  value: ProductMetadataValue,
): Record<string, string> | undefined {
  const meta = valueToProductMetadata(value);
  const bag: Record<string, string> = {};
  if (meta.tier !== null) bag[PRODUCT_METADATA_TIER_KEY] = meta.tier;
  if (meta.access.length > 0) {
    bag[PRODUCT_METADATA_ACCESS_KEY] = meta.access.join(",");
  }
  if (meta.rank !== null) bag[PRODUCT_METADATA_RANK_KEY] = String(meta.rank);
  return Object.keys(bag).length > 0 ? bag : undefined;
}

/**
 * Build the `metadata` patch for a Product **update**. Always carries
 * all three keys — an empty value is sent as `""` so Stripe's metadata
 * merge deletes the key. Other (non-pricing-v2) metadata keys on the
 * Product are untouched by the merge.
 */
export function buildUpdateMetadataPatch(value: ProductMetadataValue): Record<string, string> {
  const meta = valueToProductMetadata(value);
  return {
    [PRODUCT_METADATA_TIER_KEY]: meta.tier ?? "",
    [PRODUCT_METADATA_ACCESS_KEY]: meta.access.join(","),
    [PRODUCT_METADATA_RANK_KEY]: meta.rank === null ? "" : String(meta.rank),
  };
}
